/**
 * apiClient — shared axios instance factory for authenticated API calls.
 *
 * Why this exists:
 *   The Capacitor iOS shell runs from `capacitor://localhost`, so
 *   cross-origin cookies to `flythouse.com` are frequently dropped.
 *   Every authenticated call therefore needs a `Authorization: Bearer
 *   <token>` header, pulled from the same store that `UserAuthContext`
 *   populates on login. Multiple modules used to hand-roll their own
 *   `axios.create(...)` and silently skipped the Bearer header — that's
 *   why the Founder-only widgets, story stats, and inbox came up blank
 *   on native iOS while working fine in the browser (where cookies
 *   still land).
 *
 *   `createApiClient()` returns an axios instance that:
 *     • Points at `${REACT_APP_BACKEND_URL}/api` by default
 *     • Sends `withCredentials: true` so browser cookies still work
 *     • On every request attaches `Authorization: Bearer <token>` from
 *       `getTokenSync()` — no-op on web where the token is null
 *     • On every response captures a fresh `session_token` from the
 *       payload (if the endpoint returns one, e.g. auth refresh) and
 *       persists it back into Capacitor Preferences via `saveToken`
 *     • Has a global 20s request timeout — the axios default of "never"
 *       used to hang the UI indefinitely on flaky 3G / hotel wifi. A
 *       hard timeout surfaces failures early so the app can show its
 *       "try again" state instead of a permanent spinner.
 *
 * All modules that need authenticated API calls should use this
 * factory instead of calling `axios.create` directly.
 */
import axios from 'axios';
import { getTokenSync, saveToken } from './nativeSession';

const DEFAULT_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_TIMEOUT_MS = 20_000;

export function createApiClient(baseURL = DEFAULT_BASE) {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT_MS,
  });

  instance.interceptors.request.use((config) => {
    const token = getTokenSync();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use((res) => {
    const tok = res?.data?.session_token;
    if (tok) saveToken(tok);
    return res;
  });

  return instance;
}

// iter358 · Default export = a pre-configured client so callers can just
// `import userApi from './apiClient'` (used by AuthCallback + others).
// Previously only the factory was exported which meant those imports
// destructured `undefined` and silently no-op'd on native iOS.
const userApi = createApiClient();
export default userApi;
